import { useCallback, useState, useEffect, useContext, useMemo } from "react";

import { TasksContext } from "../../context/tasks/store";
import { TaskForm } from "./TaskForm.component";
import { allCoffrets, updateCoffret, addCoffret } from "../../api/coffrets";
import { allData } from "../../api/dataApi";

async function postImage(image: any, description: string) {
  const token = localStorage.getItem("token");
  const formData = new FormData();
  formData.append("image", image);
  formData.append("description", `${description}.jpg`);

  const resp = await fetch("/api/image", {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });

  const raw = await resp.text();
  let parsed: any = undefined;
  try {
    parsed = raw ? JSON.parse(raw) : undefined;
  } catch {}

  return {
    ok: resp.ok,
    status: resp.status,
    data: parsed,
    raw,
  };
}

async function postFacture(facture: any, description: string) {
  const token = localStorage.getItem("token");
  const formData = new FormData();
  formData.append("image", facture);
  formData.append("description", `${description}.pdf`);

  const resp = await fetch("/api/facture", {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });

  const raw = await resp.text();
  let parsed: any = undefined;
  try {
    parsed = raw ? JSON.parse(raw) : undefined;
  } catch {}

  return {
    ok: resp.ok,
    status: resp.status,
    data: parsed,
    raw,
  };
}

export const TaskFormContainer = () => {
  const {
    coffretToUpdate: coffret,
    setIsModalOpen,
    setCoffrets,
    data,
    setData,
  }: any = useContext(TasksContext);

  const [loading, setLoading] = useState(false);

  const [type, setType] = useState("");
  const [emplacement, setEmplacement] = useState("");
  const [genre, setGenre] = useState("");
  const [espece, setEspece] = useState("");
  const [faune, setFaune] = useState("");
  const [sexe, setSexe] = useState("");
  const [quantite, setQuantite] = useState(1);
  const [prixAchat, setPrixAchat] = useState("0");
  const [dateCapture, setDateCapture] = useState("");
  const [typeAchat, setTypeAchat] = useState("");
  const [detailsCapture, setDetailsCapture] = useState("");
  const [detailsAchat, setDetailsAchat] = useState("");
  const [numeroCoffret, setNumeroCoffret] = useState("");
  const [numeroFacture, setNumeroFacture] = useState("");
  const [numeroOrdre, setNumeroOrdre] = useState((data?.maxOrder + 1) || 0);
  const [file, setFile] = useState<any>();
  const [facture, setFacture] = useState<any>();
  const [cites, setCites] = useState("");

  const formValues = {
    type,
    file,
    emplacement,
    genre,
    espece,
    faune,
    sexe,
    quantite,
    prixAchat,
    dateCapture,
    typeAchat,
    detailsCapture,
    detailsAchat,
    numeroCoffret,
    numeroFacture,
    numeroOrdre,
    cites,
  };

  const handleSubmit = useCallback(
    async (e: React.SyntheticEvent) => {
      e.preventDefault();
      setLoading(true);

      // "SANS" -> vide pour le backend
      const nfRaw = (numeroFacture || "").trim();
      const nf = nfRaw.toUpperCase() === "SANS" ? "" : nfRaw;

      const newValues = {
        ...formValues,
        numeroFacture: nf,
        prixAchat: parseFloat(formValues.prixAchat),
      };

      try {
        let targetId = coffret?._id ?? coffret?.id ?? null;

        // 1) create / update du coffret
        if (coffret) {
          const resp = await updateCoffret(newValues as any, targetId);
          if (!resp.ok) throw new Error(resp.message || "Update failed");
        } else {
          const resp = await addCoffret(newValues as any);
          if (!resp.ok) throw new Error(resp.message || "Create failed");

          // si un jour le backend renvoie l'id, on le récupère
          targetId =
            resp?.data?.data?._id ??
            resp?.data?.data?.id ??
            resp?.data?._id ??
            resp?.data?.id ??
            targetId;
        }

        // 2) upload image si besoin
        if (file) {
          const imgResp = await postImage(file, numeroCoffret);
          if (!imgResp.ok) {
            console.warn("[postImage] FAILED:", imgResp.status, imgResp.raw);
          }
        }

        // 3) upload facture si besoin
        let storedFactureFilename = "";
        if (facture && nf) {
          const factResp = await postFacture(facture, nf);

          if (!factResp.ok) {
            console.warn("[postFacture] FAILED:", factResp.status, factResp.raw);
          } else {
            storedFactureFilename =
              factResp?.data?.data?.storedAs ||
              factResp?.data?.storedAs ||
              "";
            console.log("[FACTURE] storedAs =", storedFactureFilename);
          }
        }

        // 4) si on a le vrai nom du fichier, on le pousse explicitement dans le coffret
        if (storedFactureFilename) {
          if (targetId) {
            const linkResp = await updateCoffret(
              {
                numeroFacture: nf,
                factureFilename: storedFactureFilename,
              } as any,
              targetId
            );

            if (!linkResp.ok) {
              console.warn("[factureFilename link] FAILED:", linkResp.status, linkResp.message);
            }
          } else {
            // fallback si create n'a pas renvoyé l'id : on tente de retrouver le coffret fraîchement créé
            const resFind = await allCoffrets();
            const list = (resFind?.data as any)?.data || [];

            const matched = [...list]
              .reverse()
              .find(
                (x: any) =>
                  String(x?.numeroCoffret || "") === String(numeroCoffret || "") &&
                  Number(x?.numeroOrdre || 0) === Number(numeroOrdre || 0) &&
                  String(x?.genre || "") === String(genre || "") &&
                  String(x?.espece || "") === String(espece || "")
              );

            if (matched?._id) {
              const linkResp = await updateCoffret(
                {
                  numeroFacture: nf,
                  factureFilename: storedFactureFilename,
                } as any,
                matched._id
              );

              if (!linkResp.ok) {
                console.warn("[factureFilename fallback link] FAILED:", linkResp.status, linkResp.message);
              }
            } else {
              console.warn("[factureFilename] Impossible de retrouver le coffret créé pour le lier.");
            }
          }
        }

        // 5) refresh coffrets
        const res = await allCoffrets();
        if (res.ok) setCoffrets((res?.data as any)?.data || []);
        else console.warn("[allCoffrets] FAILED:", res.status, res.message);

        // 6) refresh data
        const response = await allData();
        if (response.ok) setData(response?.data);
        else console.warn("[allData] FAILED:", response.status, response.message);

        setIsModalOpen();
      } catch (err) {
        console.warn("[TaskForm submit] error:", err);
      } finally {
        setLoading(false);
      }
    },
    [
      coffret,
      setCoffrets,
      setIsModalOpen,
      formValues,
      file,
      facture,
      numeroCoffret,
      numeroFacture,
      numeroOrdre,
      genre,
      espece,
      setData,
    ]
  );

  // Pré-remplissage
  useEffect(() => {
    if (coffret) {
      setType(coffret.type ?? "");
      setEmplacement(coffret.emplacement ?? "");
      setGenre(coffret.genre ?? "");
      setEspece(coffret.espece ?? "");
      setFaune(coffret.faune ?? "");
      setSexe(coffret.sexe ?? "");
      setQuantite(coffret.quantite ?? 1);
      setPrixAchat(coffret.prixAchat ?? "0");
      setDateCapture(coffret.dateCapture ?? "");
      setTypeAchat(coffret.typeAchat ?? "");
      setDetailsCapture(coffret.detailsCapture ?? "");
      setDetailsAchat(coffret.detailsAchat ?? "");
      setNumeroCoffret(coffret.numeroCoffret ?? "");
      setNumeroFacture((coffret.numeroFacture ?? "").trim() || "SANS");
      setNumeroOrdre(coffret.numeroOrdre ?? ((data?.maxOrder + 1) || 0));
      setCites(coffret.cites ?? "");
    }
  }, [coffret, data?.maxOrder]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
      const { value, name } = e.target;

      if (name === "type") setType(value);
      else if (name === "emplacement") setEmplacement(value);
      else if (name === "cites") setCites(value);
      else if (name === "genre") setGenre(value.toUpperCase());
      else if (name === "espece") setEspece(value.toUpperCase());
      else if (name === "faune") setFaune(value);
      else if (name === "sexe") setSexe(value);
      else if (name === "quantite") setQuantite(parseInt(value));
      else if (name === "prixAchat") {
        const regex = /([0-9]*[\.|\,]{0,1}[0-9]{0,2})/s;
        const val = value.match(regex);
        if (val) setPrixAchat(val[0]);
      } else if (name === "dateCapture") setDateCapture(value);
      else if (name === "typeAchat") setTypeAchat(value);
      else if (name === "detailsCapture") setDetailsCapture(value.toUpperCase());
      else if (name === "detailsAchat") setDetailsAchat(value.toUpperCase());
      else if (name === "numeroCoffret") setNumeroCoffret(value);
      else if (name === "numeroFacture") setNumeroFacture(value);
      else if (name === "numeroOrdre") setNumeroOrdre(parseInt(value));
    },
    []
  );

  const checkSubmitValue = useCallback(() => {
    if (loading) return "Chargement...";
    return coffret ? "Mettre à jour" : "Créer";
  }, [loading, coffret]);

  const submitValue = useMemo(checkSubmitValue, [checkSubmitValue]);

  const fileSelected = (event: any) => {
    const selected = event.target.files?.[0];
    if (selected) setFile(selected);
  };

  const factureSelected = (event: any) => {
    const selected = event.target.files?.[0];
    if (selected) setFacture(selected);
  };

  return (
    <TaskForm
      handleSubmit={handleSubmit}
      handleChange={handleChange}
      lastOrder={data?.maxOrder}
      loading={loading}
      {...formValues}
      submitValue={submitValue}
      fileSelected={fileSelected}
      factureSelected={factureSelected}
      facture={facture}
    />
  );
};